import { useLayoutEffect, useRef } from 'react';

import { C_Regexp } from '@/ts/constants/C_Regexp';

export const useCursorPreservation = (
  inputRef: React.RefObject<HTMLInputElement | null>,
  value: string
) => {
  const cursorState = useRef({
    position: 0,
    nonSeparatorCount: 0,
    lastValue: ''
  });

  const countNonSeparators = (val: string, upTo: number) => {
    let count = 0;
    for (let i = 0; i < upTo; i++) {
      if (C_Regexp.numberOrDot.test(val[i])) {
        count++;
      }
    }
    return count;
  };

  const findPositionForCount = (val: string, targetCount: number) => {
    let count = 0;
    for (let i = 0; i < val.length; i++) {
      if (C_Regexp.numberOrDot.test(val[i])) {
        count++;
      }
      if (count === targetCount) {
        return i + 1;
      }
    }
    return val.length;
  };

  const captureCursor = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const start = e.target.selectionStart || 0;
    const removedDiff = value.length - newValue.length;

    // Check if a comma was just deleted
    // Condition 1: Length decreased by 1
    // Condition 2: The character at current cursor position in OLD value was a comma
    // Condition 3: The rest of the string matches (deletion, not replacement)
    // Note: 'start' is the position AFTER the deletion, so it's the index where the char WAS
    if (
      removedDiff === 1 &&
      value[start] === ',' &&
      value.slice(0, start) + value.slice(start + 1) === newValue
    ) {
      // Logic: User deleted a comma. We want to delete the number BEFORE the comma instead.
      // 'newValue' is currently "12345" (from "12,345").
      // We want to remove the char at 'start - 1' from 'newValue'.
      if (start > 0) {
        const adjustedValue =
          newValue.slice(0, start - 1) + newValue.slice(start);

        // Adjust cursor to be before the deleted number
        cursorState.current = {
          position: start - 1,
          nonSeparatorCount: countNonSeparators(adjustedValue, start - 1),
          lastValue: adjustedValue
        };

        return adjustedValue;
      }
    }

    cursorState.current = {
      position: start,
      nonSeparatorCount: countNonSeparators(newValue, start),
      lastValue: newValue
    };
    return null;
  };

  useLayoutEffect(() => {
    if (inputRef.current && value !== cursorState.current.lastValue) {
      if (cursorState.current.lastValue !== '') {
        // Only restore if not first render or full reset
        const newPos = findPositionForCount(
          value,
          cursorState.current.nonSeparatorCount
        );
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }
  }, [inputRef, value]);

  return { captureCursor };
};
